/* eslint-disable dot-notation */
/* eslint-disable import/no-unresolved */
import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { Typography, Grid, TextField, Stack, Autocomplete, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { MuiTelInput } from 'mui-tel-input';
import { countries } from 'src/data/countryData/countries';
import { state as allState } from 'src/data/countryData/states';
import { SelectBm } from 'src/components/@material-extend';
import { stateLabel } from 'src/data/countryData/stateLabel';
import { useAddProducerByPartner } from 'src/components/api/mutations/hooks';

export const Onboarding = () => {
  const navigate = useNavigate();
  const [country, setCountry] = useState({ label: 'Argentina', value: 'AR' });
  const [state, setState] = useState(allState['AR'][0].value);
  const [gender, setGender] = useState('');
  const [loading, setLoading] = useState(false);
  const stateDesc = stateLabel[country.value] ? stateLabel[country.value] : 'State';

  const { mutateAsync: addProducer } = useAddProducerByPartner();

  const RGENDER = { Male: 'MALE', Female: 'FEMALE', Other: 'OTHER' };
  const GENDER = Object.keys(RGENDER); // ["Male", "Female", "Other"]

  const rancherSchema = yup.object().shape({
    givenName: yup.string().trim().min(2, 'Enter valid first name').required('First name is required'),
    middleName: yup.string().trim(),
    familyName: yup.string().trim().min(2, 'Enter valid last name').required('Last name is required'),
    email: yup.string().trim().email('Enter valid email').required('Email is required'),
    phoneNumber: yup.string().min(8, 'Enter valid phone number').required('Phone number is required'),
    postalCode: yup.string().required('Postal code is required'),
    partnerId: yup.string().min(4).max(6).required('Partner ID is required'),
    organization: yup.string().required('Organization is required'),
    role: yup.string().required('Role is required'),
  });

  const formik = useFormik({
    initialValues: {
      givenName: '',
      middleName: '',
      familyName: '',
      phoneNumber: '',
      phoneCode: '',
      localNumber: '',
      email: '',
      address: '',
      postalCode: '',
      role: '',
      organization: '',
      city: '',
      partnerId: '1002',
    },
    validationSchema: rancherSchema,
    onSubmit: async (values) => {
      if (!gender) {
        alert('Please select gender');
        return;
      }
      try {
        setLoading(true);

        const allValues = {
          ...values,
          country: country.value,
          state,
          gender: RGENDER[gender], // Map to backend format
          phoneNumber: values.localNumber,
          phoneCode: values.phoneCode,
        };
        delete allValues.localNumber;

        // console.log('Submitting:', allValues);

        await addProducer(allValues);
        navigate('../AddRanch', { state: { formData: values } });
      } catch (error) {
        console.error(error);
        alert('Failed to add Rancher');
      } finally {
        setLoading(false);
      }
    },
  });

  const handlePhoneChange = (value, info) => {
    formik.setFieldValue('phoneNumber', value);

    const countryCode = value.replace(info.nationalNumber, '');
    const localNumber = info.nationalNumber;

    formik.setFieldValue('phoneCode', countryCode ?? '');
    formik.setFieldValue('localNumber', localNumber ?? '');
  };

  const onClick1 = () => {
    navigate('../AddRanch', { state: { formData: formik.values } });
  };

  const isDisabled = loading;

  return (
    <Stack sx={{ margin: '0 auto' }}>
      <form onSubmit={formik.handleSubmit}>
        <Stack direction="row" spacing={5} sx={{ margin: '0 4rem', mb: '2rem' }}>
          <Grid flex={1} />
          <Typography style={{ fontSize: 30 }}>
            <strong>Add Rancher Details</strong>
          </Typography>
          <Grid flex={1} />
          <Button
            style={{ backgroundColor: '#004752', color: '#fff' }}
            onClick={onClick1}
            disabled={isDisabled}
          >
            Add Ranch
          </Button>
        </Stack>

        <Stack spacing={3}>
          <TextField
            required
            name="partnerId"
            label="Partner Id"
            style={{ width: '60%', alignSelf: 'center' }}
            value={formik.values['partnerId']}
            onChange={formik.handleChange}
            error={formik.touched['partnerId'] && Boolean(formik.errors['partnerId'])}
            helperText={formik.touched['partnerId'] && formik.errors['partnerId']}
          />
          {/* Name Fields */}
          <Stack direction="row" width="60%" spacing={2} alignItems="baseline" alignSelf="center">
            <TextField
              required
              name="givenName"
              label="First Name"
              fullWidth
              value={formik.values.givenName}
              onChange={formik.handleChange}
              error={formik.touched.givenName && Boolean(formik.errors.givenName)}
              helperText={formik.touched.givenName && formik.errors.givenName}
            />
            <TextField
              name="middleName"
              label="Middle Name"
              fullWidth
              value={formik.values.middleName}
              onChange={formik.handleChange}
              error={formik.touched.middleName && Boolean(formik.errors.middleName)}
              helperText={formik.touched.middleName && formik.errors.middleName}
              required
            />
            <TextField
              required
              name="familyName"
              label="Last Name"
              fullWidth
              value={formik.values.familyName}
              onChange={formik.handleChange}
              error={formik.touched.familyName && Boolean(formik.errors.familyName)}
              helperText={formik.touched.familyName && formik.errors.familyName}
            />
          </Stack>

          {/* Phone & Gender */}
          <Stack alignItems="baseline" alignSelf="center" direction="row" spacing={2} width="60%">
            <MuiTelInput
              required
              style={{ backgroundColor: '#fff', flex: 1 }}
              FormHelperTextProps={{ sx: { bgcolor: '#fff', margin: 0 } }}
              value={formik.values.phoneNumber}
              onChange={handlePhoneChange}
              onlyCountries={['AR', 'BR', 'UY', 'MX', 'PY', 'KE', 'US', 'CR', 'CO']}
              defaultCountry="AR"
              forceCallingCode
              disableFormatting
              label="Phone number"
              inputProps={{ maxLength: 13 }}
              error={Boolean(formik.touched.phoneNumber && formik.errors.phoneNumber)}
              helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
            />
            <SelectBm
              required
              label="Select Gender*"
              value={gender}
              onChange={({ target }) => setGender(target.value)}
              sx={{ borderRadius: 1 }}
              containerSx={{ flex: 1 }}
              menuSx={{ width: '50%', flex: 1 }}
              list={GENDER} // Correct gender list
            />
          </Stack>

          {/* Email */}
          <Stack alignItems="center">
            <TextField
              required
              name="email"
              label="Email"
              style={{ width: '60%' }}
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Stack>

          {/* Country & State */}
          <Stack direction="row" spacing={2} justifyContent="center">
            <Autocomplete
              disablePortal
              fullWidth
              sx={{ bgcolor: '#fff', borderRadius: 1, width: '28%' }}
              options={countries}
              disableClearable
              defaultValue={country}
              onChange={(_, v) => {
                setCountry(v);
                setState(allState[v.value][0].value);
              }}
              value={country}
              renderInput={(params) => <TextField required {...params} label="Country" />}
            />
            <SelectBm
              required
              label={`Select ${stateDesc}*`}
              value={state}
              fullWidth
              onChange={({ target }) => setState(target.value)}
              sx={{ width: '23vw', borderRadius: 1 }}
              list={allState[country.value] || []}
            />
          </Stack>

          {/* Role & Organisation */}
          <Stack direction="row" spacing={2} justifyContent="center">
            <TextField
              required
              name="role"
              label="Role"
              style={{ width: '28%' }}
              value={formik.values.role}
              onChange={formik.handleChange}
              error={formik.touched.role && Boolean(formik.errors.role)}
              helperText={formik.touched.role && formik.errors.role}
            />
            <TextField
              required
              name="organization"
              label="Organisation"
              style={{ width: '30%' }}
              value={formik.values.organization}
              onChange={formik.handleChange}
              error={formik.touched.organization && Boolean(formik.errors.organization)}
              helperText={formik.touched.organization && formik.errors.organization}
            />
          </Stack>

          {/* Address */}
          <Stack alignItems="center">
            <TextField
              required
              name="address"
              label="Legal Address"
              style={{ width: '60%' }}
              value={formik.values.address}
              onChange={formik.handleChange}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
            />
          </Stack>

          {/* City & Postal */}
          <Stack direction="row" spacing={2} justifyContent="center">
            <TextField
              required
              name="city"
              label="City"
              style={{ width: '28%' }}
              value={formik.values.city}
              onChange={formik.handleChange}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={formik.touched.city && formik.errors.city}
            />
            <TextField
              required
              name="postalCode"
              label="Postal Code"
              style={{ width: '30%' }}
              value={formik.values.postalCode}
              onChange={formik.handleChange}
              error={formik.touched.postalCode && Boolean(formik.errors.postalCode)}
              helperText={formik.touched.postalCode && formik.errors.postalCode}
            />
          </Stack>
        </Stack>

        <Stack style={{ margin: '2rem' }}>
          <LoadingButton
            style={{ backgroundColor: '#005249', width: '40%', margin: 'auto' }}
            loading={loading}
            fullWidth
            variant="contained"
            type="submit"
          >
            Submit
          </LoadingButton>
        </Stack>
      </form>
    </Stack>
  );
};
